import { useMemo } from 'react';
import type { Player } from '@goodtime/shared';
import { applyOverride, onSlot, parseFeatures, parseFeatureThemes, type FeatureName } from './flags';

export interface Features {
  /** True when the feature is on for this player. */
  has(name: FeatureName): boolean;
  /** True when a feature that runs on some slots only (Royal Road) is on
   *  for this player on this slot, by the server's list for them. */
  onSlot(name: FeatureName, themeId: string): boolean;
  /** Every feature on, for logging and the dev tools. */
  list: FeatureName[];
}

/** The player's feature flags, with the DEV {@code ?ff=} override applied.
 *  No player yet (still connecting, or offline) means no features. */
export function useFeatures(player: Player | null): Features {
  const raw = player?.features;
  const rawThemes = player?.featureThemes;
  return useMemo(() => {
    const search = typeof window === 'undefined' ? '' : window.location.search;
    const on = applyOverride(parseFeatures(raw), search, import.meta.env.DEV);
    const themes = parseFeatureThemes(rawThemes);
    return {
      has: (name: FeatureName) => on.has(name),
      onSlot: (name: FeatureName, themeId: string) => onSlot(on, themes, name, themeId),
      list: [...on],
    };
  }, [raw, rawThemes]);
}
